import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { COBRA_API } from "../../../api/config";
import {
  DeviceConfiguration,
  DeviceConfigurationUpdateDtoDeviceConfigurationUpdateResponseEnum,
} from "../../../cobra-backend-client";
import { CustomAlert } from "../../common/CustomAlert";
import { disableDeviceStatusMonitor } from "../../common/deviceStatusMonitor";

interface Props {
  deviceId: number;
  updatedDeviceConfiguration: DeviceConfiguration;
  setUpdatedDeiceConfiguration: React.Dispatch<
    React.SetStateAction<DeviceConfiguration>
  >;
  showAngleCalibrationProgress: boolean;
  setShowAngleCalibrationProgress: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  platformAngle: number | undefined;
}
const AngleCalibration = (props: Props) => {
  const dispatch = useDispatch();

  const [platformAngleDisplayValue, setPlatformAngleDisplayValue] =
    useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [isErrorAlert, setIsErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  useEffect(() => {
    if (props.platformAngle != null) {
      const angleValue =
        props.platformAngle != 0 ? props.platformAngle / 100 : 0;
      setPlatformAngleDisplayValue(angleValue.toFixed(1) + "°");
    }
  }, [props.platformAngle]);

  const onSetZeroClick = () => {
    props.setShowAngleCalibrationProgress(true);
    updateAngleCallibration();
  };

  const updateAngleCallibration = async () => {
    if (props.platformAngle == null || props.deviceId == null) return;
    try {
      const signedDeviceConfiguration =
        await COBRA_API.DeviceConfiguration.getSignedDeviceConfiguration(
          +props.deviceId
        );
      const deviceConfiguration = signedDeviceConfiguration.data.configuration;
      if (!deviceConfiguration) {
        setAlertMessage("Configuration can not be exported");
        setIsErrorAlert(true);
        setShowAlert(true);
        return;
      }
      const angleCorrection = deviceConfiguration?.imu?.angleCorrection ?? 0;
      const newAngleCorrection = angleCorrection - props.platformAngle;

      const newDeviceConfiguration: DeviceConfiguration = {
        ...deviceConfiguration,
        imu: {
          ...deviceConfiguration?.imu,
          angleCorrection: newAngleCorrection,
        },
      };
      updateDeviceConfiguration(props.deviceId, newDeviceConfiguration);
      props.setUpdatedDeiceConfiguration(newDeviceConfiguration);
    } catch (e) {
      props.setShowAngleCalibrationProgress(false);
      setAlertMessage("Configuration can not be exported");
      setIsErrorAlert(true);
      setShowAlert(true);
    }
  };

  const updateDeviceConfiguration = async (
    deviceId: number,
    deviceConfiguration: DeviceConfiguration
  ) => {
    try {
      disableDeviceStatusMonitor(props.deviceId, dispatch);

      const deviceConfigurationUpdateDto =
        await COBRA_API.DeviceConfiguration.updateDeviceConfiguration(
          deviceId,
          deviceConfiguration
        );
      if (
        deviceConfigurationUpdateDto.data.deviceConfigurationUpdateResponse ===
        DeviceConfigurationUpdateDtoDeviceConfigurationUpdateResponseEnum.NoErr
      ) {
        setIsErrorAlert(false);
        setAlertMessage("Angle was calibrated successfully");
      } else {
        setIsErrorAlert(true);
        setAlertMessage("ERROR: Angle was not calibrated");
      }
    } catch (e) {
      setIsErrorAlert(true);
      setAlertMessage("ERROR: Angle was not calibrated");
    }
    props.setShowAngleCalibrationProgress(false);
    setShowAlert(true);
  };

  return (
    <>
      <Grid container flexGrow={1}>
        <Grid item xs={6} sm={6}>
          <Typography>
            Angle calibration
            {props.showAngleCalibrationProgress && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={6} textAlign="right">
          {props.platformAngle != null && (
            <>
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Typography sx={{ mr: 1 }}>
                  {platformAngleDisplayValue}
                </Typography>

                <Button
                  variant="contained"
                  size="small"
                  onClick={onSetZeroClick}
                  disabled={
                    props.showAngleCalibrationProgress ||
                    !props.updatedDeviceConfiguration.imu?.plSensEnable
                  }
                >
                  Set '0'
                </Button>
              </Box>
            </>
          )}
        </Grid>
        {showAlert && (
          <CustomAlert
            autoHideDurationMs={5000}
            onAlertClose={() => {
              setShowAlert(false);
              setAlertMessage("");
            }}
            alertSeverity={isErrorAlert ? "error" : "success"}
            alertText={alertMessage}
          />
        )}
      </Grid>
    </>
  );
};

export { AngleCalibration };
